import { useEffect, useRef, useState } from 'react';

// Lớp nền động theo mùa + ngày/đêm + thời tiết.
// Nguồn thời tiết: Open-Meteo (miễn phí, không cần API key), cố định Hà Nội.
const HANOI = { lat: 21.0278, lon: 105.8342 };

// Mùa theo miền Bắc VN.
function seasonOf(month) {
  if (month === 12 || month <= 2) return 'winter';
  if (month <= 4) return 'spring';
  if (month <= 8) return 'summer';
  return 'autumn';
}

// Mã thời tiết WMO -> loại mưa/tuyết.
function precipFromCode(code) {
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82, 95, 96, 99].includes(code)) return 'rain';
  if ([71, 73, 75, 77, 85, 86].includes(code)) return 'snow';
  return 'none';
}

function isDarkMode() {
  const t = document.documentElement.getAttribute('data-theme');
  if (t === 'dark') return true;
  if (t === 'light') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export default function SeasonalBackground() {
  const [on, setOn] = useState(() => localStorage.getItem('ft-fx') !== 'off');
  const [wx, setWx] = useState(null); // { isDay, code }
  const canvasRef = useRef(null);

  // Nghe nút bật/tắt hiệu ứng (FxToggle phát sự kiện này).
  useEffect(() => {
    const h = () => setOn(localStorage.getItem('ft-fx') !== 'off');
    window.addEventListener('ft-fx-change', h);
    return () => window.removeEventListener('ft-fx-change', h);
  }, []);

  // Lấy thời tiết Hà Nội, làm mới mỗi 30 phút. Offline -> dựa vào giờ máy.
  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${HANOI.lat}&longitude=${HANOI.lon}&current=weather_code,is_day,temperature_2m`;
        const r = await fetch(url);
        const j = await r.json();
        if (alive && j.current) setWx({ isDay: j.current.is_day === 1, code: j.current.weather_code, temp: j.current.temperature_2m });
      } catch (e) {
        /* bỏ qua, dùng giờ máy */
      }
    }
    load();
    const id = setInterval(load, 30 * 60 * 1000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  // Suy ra trạng thái hiển thị.
  const now = new Date();
  const season = seasonOf(now.getMonth() + 1);
  const hour = now.getHours();
  const isDay = wx ? wx.isDay : hour >= 6 && hour < 18;
  let precip = wx ? precipFromCode(wx.code) : 'none';
  if (precip === 'none' && season === 'winter') precip = 'snow'; // đông -> tuyết nhẹ
  const clearDay = isDay && (!wx || [0, 1, 2].includes(wx.code)); // ban ngày, trời quang/ít mây
  const sunny = clearDay && (season === 'summer' || season === 'spring');
  // Nắng NÓNG: trời quang ban ngày + nhiệt độ cao (≥ 32°C) -> thêm ánh nắng ấm phủ toàn giao diện.
  const HOT_C = 32;
  const hotSunny = clearDay && wx && typeof wx.temp === 'number' && wx.temp >= HOT_C;

  // Hạt rơi (tuyết/mưa) bằng canvas — tinh tế, ít hạt.
  useEffect(() => {
    if (!on || precip === 'none') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dark = isDarkMode();
    const isSnow = precip === 'snow';
    let raf, w, h;
    function resize() {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    const N = isSnow ? 38 : 55;
    const parts = Array.from({ length: N }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      r: isSnow ? 1.4 + Math.random() * 1.8 : 0,
      len: isSnow ? 0 : 7 + Math.random() * 10,
      vx: isSnow ? -0.3 + Math.random() * 0.6 : -1,
      vy: isSnow ? 0.5 + Math.random() * 0.8 : 6 + Math.random() * 4,
      o: 0.22 + Math.random() * 0.33,
    }));
    const snowColor = dark ? '255,255,255' : '150,180,215';
    const rainColor = dark ? '150,180,210' : '90,130,175';

    function draw() {
      ctx.clearRect(0, 0, w, h);
      for (const p of parts) {
        if (isSnow) {
          ctx.beginPath();
          ctx.fillStyle = `rgba(${snowColor},${p.o})`;
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.strokeStyle = `rgba(${rainColor},${p.o})`;
          ctx.lineWidth = 1;
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x + p.vx, p.y + p.len);
          ctx.stroke();
        }
        p.x += p.vx;
        p.y += p.vy;
        if (p.y > h) {
          p.y = -10;
          p.x = Math.random() * w;
        }
        if (p.x < 0) p.x = w;
      }
      if (!reduce) raf = requestAnimationFrame(draw);
    }
    draw();
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [on, precip, wx]);

  if (!on) return null;

  return (
    <>
      <div className="fx-layer" aria-hidden="true">
        {(sunny || hotSunny) && <div className="fx-sun" />}
        {hotSunny && <div className="fx-rays" />}
        {!isDay && (
          <>
            <div className="fx-night" />
            <div className="fx-stars" />
            <div className="fx-moon" />
          </>
        )}
        {precip !== 'none' && <canvas ref={canvasRef} className="fx-canvas" />}
      </div>
      {/* Nắng nóng: ánh nắng ấm phủ NHẸ toàn giao diện (trên nội dung, không chặn thao tác). */}
      {hotSunny && <div className="fx-warmth" aria-hidden="true" />}
    </>
  );
}
